/* eslint-disable no-param-reassign */
import { FormInstance } from 'antd';
import { useForm } from 'antd/es/form/Form';
import moment from 'moment-timezone';
import React, { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import draftToHtml from 'draftjs-to-html';
import { ROWS } from '../components/Pages/Reports/constants';
import {
  IReport,
  IReportLoan,
  IReportSettingRow,
  IReportSettings,
  useReportClosingCosts,
  useReportCreate,
  useReportId,
  useReportLoanClosingCosts,
  useReportLoanCreate,
  useReportLoanDelete,
  useReportLoans,
  useReportLoanUpdate,
  useReportSettingsById,
  useReportSettingsUpdate,
  useReportTrackActivities,
  useReportUpdate
} from '../hooks/reports';
import { JsonResult } from '../types';
import { EnumReportTypes, ILoanValue } from '../types/reports';
import { prepareLoanDataFromForm } from '../utils/prepareData';
import { toFloat } from '../utils/text';
import { ILoanClosingCost, ISettingItem } from '../hooks/users';

interface ReportsContext {
  previewLoading: boolean;
  form?: FormInstance;
  errors?: string[];
  reportId?: string;
  setReportId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  isBorrower?: boolean;
  setIsBorrower?: (value: boolean) => void;
  loans?: IReportLoan[];
  getLoans?: () => void;
  loansLoading?: boolean;
  report?: IReport;
  setReport?: React.Dispatch<React.SetStateAction<IReport | undefined>>;
  getReport?: () => Promise<IReport | null>;
  reportLoading?: boolean;
  settings?: IReportSettings;
  getSettings?: (reportType: EnumReportTypes) => Promise<void>;
  settingsLoading?: boolean;
  reportClosingCosts?: ISettingItem[];
  setReportClosingCosts?: React.Dispatch<React.SetStateAction<ISettingItem[] | undefined>>;
  reportLoanClosingCosts?: ILoanClosingCost[];
  closingCostsLoading: boolean;
  handleLoansUpdate?: (event?: unknown, withLoading?: boolean) => void;
  handleReportUpdate?: (status?: string, callback?: () => void) => void;
  handleReportSettingsUpdate?: (type: string) => void;
  setLoansLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  getReportClosingCosts?: () => Promise<ISettingItem[]>;
  getReportLoanClosingCosts?: () => void;
  setPreviewLoading: (value: boolean) => void;
  setClosingCostsLoading: (value: boolean) => void;
}

const defaultValue = {
  loansLoading: false,
  reportLoading: false,
  previewLoading: false,
  closingCostsLoading: false,
  setPreviewLoading: (value: boolean) => value,
  setClosingCostsLoading: (value: boolean) => value,
};

export const ReportsContext = createContext<ReportsContext>(defaultValue);

interface IReportsProvider extends PropsWithChildren {
  value?: ReportsContext;
}
const ReportsProvider = ({ children, value }: IReportsProvider) => {
  const { id } = useParams()
  const [form] = useForm();
  const [errors, setErrors] = useState<string[]>();
  const [report, setReport] = useState<IReport>();
  const [loans, setLoans] = useState<IReportLoan[]>();
  const [reportId, setReportId] = useState<string>();
  const [loansLoading, setLoansLoading] = useState<boolean>(false);
  const reportLoans = useReportLoans();
  const reportById = useReportId();
  const reportSettingsById = useReportSettingsById();
  const navigate = useNavigate();
  const reportUpdate = useReportUpdate();
  const reportLoanCreate = useReportLoanCreate();
  const reportLoanUpdate = useReportLoanUpdate();
  const reportLoanDelete = useReportLoanDelete();
  const reportSettingsUpdate = useReportSettingsUpdate();
  const reportCreate = useReportCreate();
  const reportTrackActivities = useReportTrackActivities();
  const reportClosingCostsByReportId = useReportClosingCosts();
  const reportLoanClosingCostsByReportId = useReportLoanClosingCosts();
  const [reportClosingCosts, setReportClosingCosts] = useState<ISettingItem[]>();
  const [reportLoanClosingCosts, setReportLoanClosingCosts] = useState<ILoanClosingCost[]>();
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [closingCostsLoading, setClosingCostsLoading] = useState<boolean>(false);
  const [isBorrower, setIsBorrower] = useState<boolean>(value?.isBorrower ?? false);
  const getLoansAbortControllerRef = useRef<AbortController>();
  const updateLoansAbortControllerRef = useRef<AbortController>();

  const handleErrorsSave = (error: JsonResult) => {
    const foundErrors: string[] = [];

    error.errorFields?.forEach((item: JsonResult) => {
      item.errors?.forEach((errorMessage: string) => {
        foundErrors.push(errorMessage);
      });
    });

    setErrors(foundErrors);
  };

  const handleGetReport = async () => {
    if (!report) {
      const data = await getReport()

      if (!reportSettingsById.data && data) {
       await getSettings(data.reportType as EnumReportTypes)
      }
    }
  }

  useEffect(() => {
    if (!reportId) return;

    handleGetReport()

    if (!loans) {
      const fetchLoans = async () => {
        await getLoans()
      }

      fetchLoans()
    }
    if (!reportClosingCosts) {
      const fetchReportClosingCosts = async () => {
        await getReportClosingCosts()
      }

      fetchReportClosingCosts()
    }

    if (!reportLoanClosingCosts) {
      const fetchReportLoanClosingCosts = async () => {
        await getReportLoanClosingCosts()
      }

      fetchReportLoanClosingCosts()
    }
  }, [reportId]);

  const getReport = async () => {
    if (reportId) {
      return reportById
        .fetch(undefined, reportId)
        .then((res) => {
          setReport(res?.data);
          form?.setFieldValue(['report', 'name'], res?.data.name);
          form?.setFieldValue(['report', 'closingText'], res?.data.closingText || undefined);
          form?.setFieldValue(['report', 'welcome_message'], res?.data.welcome_message || undefined);
          form?.setFieldValue(['report', 'selectOptions'], res?.data.selectOptions || undefined);
          form?.setFieldValue(['report', 'reportType'], res?.data.reportType);

          return res?.data ?? null;
        })
        .catch(() => {
          navigate('/not-found');

          return null
        });
    }

    return null
  };

  const getSettings = async (reportType: EnumReportTypes) =>
    // @ts-ignore - It's okay to ignore this error
    reportSettingsById.fetch({ reportType: reportType || EnumReportTypes.purchase }, reportId || id)
      .then((res) => {
      if (!res?.data) return;

      res.data.reportView
        .sort((a, b) => a.order - b.order)
        .forEach((row) => {
          form?.setFieldValue(['reportView', row.name], row);
        });

      if (res.data.closingCostsView) {
        const closingCostsView = res.data.closingCostsView
          .sort((a, b) => a.order - b.order)
          .reduce((acc: any, row) => {
            acc[row.name] = row;

            return acc;
          }, {});

        form?.setFieldsValue({ closingCostsView });
      }

      ROWS.forEach((row) => {
        const settingsRow = res.data.tableView.find((item) => item.name === row.name);

        form.setFieldValue(['tableView', row.name], {
          ...row,
          ...settingsRow,
        });
      });
    });

  const getReportClosingCosts = async () => reportClosingCostsByReportId.fetch(undefined, reportId)
    .then(response => {
      if (response && response.data && response.data.length > 0) {
        const data = response.data
          .sort((a, b) => a.order - b.order)

        form?.setFieldValue('reportClosingCosts', data);
        setReportClosingCosts(data);

        return data
      }

      return []
    })
    .finally(() => {
      setClosingCostsLoading(false);
    })

  const getReportLoanClosingCosts = async () => {
    // setClosingCostsLoading(true);
    await reportLoanClosingCostsByReportId.fetch(undefined, reportId)
      .then(response => {
        if (response && response.data) {
          form?.setFieldValue('reportLoanClosingCosts', response.data);
          setReportLoanClosingCosts(response.data);
        }
      })
      .finally(() => {
        setClosingCostsLoading(false);
      })
  }

  const getLoans = async () => {
    if (getLoansAbortControllerRef.current) {
      getLoansAbortControllerRef.current.abort();
    }

    const controller = new AbortController();

    getLoansAbortControllerRef.current = controller;

    const { signal } = controller

    reportLoans.fetch({ id: reportId || id, signal }).then((res) => {
      setLoans(res?.data);
      const newLoans: ILoanValue[] = [];

      res?.data
        .sort((a, b) => toFloat(a.order) - toFloat(b.order))
        .forEach((loan, index) => {
          const loanData = { ...loan };

          delete loanData.Report;

          const isRefinance = loan?.reportType === EnumReportTypes.refinance;
          const isFirstItemInRefinance = isRefinance && index === 0;

          const newLoan: ILoanValue = {
            ...loanData,
            startAt: loanData.startAt || moment().format('YYYY-MM-DD HH:mm:ss'),
            prepare: loanData.prepare || 3,
            searchBid: loanData.searchBid || 30,
            close: loanData.close || 21,
            currentInterestRate: loanData.currentInterestRate || loanData.interestRate,
            interestRateWithPoint: loanData.interestRateWithPoint || 0,
            totalLoanPoints: loanData.totalLoanPoints || loanData.loanPoints,
            totalCost: loanData.totalCost || loanData.closingCosts,
            otherDeductions: loanData.otherDeductions ?? 0,
          };

          ROWS.forEach((item) => {
            newLoan[item.name] = {
              value: item.name === 'closingCosts' && isFirstItemInRefinance ? '0' : loan[item.name],
            };
            if (item.subField) {
              newLoan[item.subField.name] = {
                value: loan[item.subField.name],
              };
            }
          });

          newLoans.push(newLoan);
        });

      form?.setFieldValue('loans', newLoans);
    })
  };

  const createReport = () => {
    if (reportId) return;

    form
      .validateFields()
      .then((values) => {
        const { tableView } = values;
        const loanColor: JsonResult = {};

        Object.entries(tableView).forEach(([key, settingsValue]) => {
          const row = settingsValue as JsonResult;

          if (!row?.color) return;

          loanColor[key] = row.color;
        });

        reportCreate.fetch({ name: values.report.name, status: 'created', loanColor }).then((res) => {
          if (!res?.data.id) return;

          setReportId(res?.data.id);
          const preparedLoans = prepareLoanDataFromForm(values.loans);

          reportTrackActivities.fetch({ id: res.data.id, type: 'created' });

          reportLoanCreate.fetch(preparedLoans, `${res.data.id}`);
        });
      })
      .catch((error) => {
        handleErrorsSave(error);
      });
  };
  // let debounceTimeout: string | number | NodeJS.Timeout | null | undefined = null;

  const handleLoansUpdate = async (event?: unknown, withLoading?: boolean) => {
    if (updateLoansAbortControllerRef.current) {
      updateLoansAbortControllerRef.current.abort();
    }

    const controller = new AbortController();

    updateLoansAbortControllerRef.current = controller;

    const { signal } = controller

    if (!loans) return;

    if (!reportId) {
      createReport();

      return;
    }

    if (withLoading) setLoansLoading(true);

    const newLoans: ILoanValue[] = form?.getFieldValue('loans')
      .map((loan: ILoanValue, index: number) => ({ ...loan, order: index + 1 }));

    const removedLoans = loans.filter((loan) => !newLoans.some((item) => item.id === loan?.id));
    const updatedLoans = newLoans.filter((loan) => loan?.id);
    let addedLoans = newLoans.filter((loan) => !loan?.id);

    try {
      if (isBorrower) {
        addedLoans = addedLoans.map((loan) => ({ ...loan, type: 'borrower' }));
      }

      await Promise.all([
        removedLoans?.map((loan) => reportLoanDelete.fetch(loan.id)),
        ...(addedLoans.length ? [reportLoanCreate.fetch(prepareLoanDataFromForm(addedLoans), reportId)] : []),
        ...(updatedLoans.length ?
          [reportLoanUpdate.fetch({ loans: prepareLoanDataFromForm(updatedLoans), signal }, reportId)] : []),
      ])

      await Promise.all([
        getReportClosingCosts?.(),
        getReportLoanClosingCosts?.(),
        getLoans(),
      ])
    } finally {
      if (withLoading) setLoansLoading(false);
    }
  };

  const handleReportUpdate = (status?: string, callback?: () => void) => {
    if (!reportId) {
      createReport();

      return;
    }

    form
      .validateFields()
      .then((values) => {
        if (typeof values.report.welcome_message === 'object') {
          const html = draftToHtml(values.report.welcome_message);

          values.report.welcome_message = html;
        }

        reportUpdate
          .fetch(
            {
              ...values.report,
              status: status || reportById.data?.data.status || 'draft',
              selectOptions: values.movingTo,
            },
            reportId
          )
          .then(callback);
      })
      .catch((error) => {
        handleErrorsSave(error);
      });
  };

  const handleReportSettingsUpdate = (type: string) => {
    if (!reportId || !reportSettingsById.data?.data) {
      createReport();

      return;
    }

    form
      .validateFields()
      .then((values) => {
        const newSettings = Object.values(values[type]).filter(
          (section) => (section as IReportSettingRow).id
        ) as IReportSettingRow[];

        if (!newSettings.length) return;

        reportSettingsUpdate.fetch({ [type]: newSettings }, reportId)
      })
      .catch((error) => {
        handleErrorsSave(error);
      });
  };

  return (
    <ReportsContext.Provider
      value={{
        ...value,
        closingCostsLoading,
        previewLoading,
        form,
        errors,
        reportId,
        setReportId,
        isBorrower,
        setIsBorrower,
        loans,
        getLoans,
        loansLoading,
        report,
        setReport,
        getReport,
        reportLoading: reportById.loading,
        settings: reportSettingsById.data?.data,
        getSettings,
        settingsLoading: reportSettingsById.loading,
        handleLoansUpdate,
        handleReportUpdate,
        handleReportSettingsUpdate,
        setLoansLoading,
        reportClosingCosts,
        setReportClosingCosts,
        reportLoanClosingCosts,
        getReportClosingCosts,
        getReportLoanClosingCosts,
        setPreviewLoading,
        setClosingCostsLoading
      }}
    >
      {children}
    </ReportsContext.Provider>
  );
};

ReportsProvider.defaultProps = {
  value: undefined,
};

export default ReportsProvider;

export const useContextReports = (): ReportsContext => useContext(ReportsContext);
